import React, {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import store, {
  AppDispatch,
  RootState,
  useAppSelector,
} from '../../redux/store';
import { Socket, io } from 'socket.io-client';
import { useDispatch, useSelector } from 'react-redux';
import {
  addMessagesToChatThread,
  addMessagesToChatThreadInitially,
  updateMessageReadStatus,
  updateInboxList,
  setActiveChat,
  removeGroupFromChatList,
  updateChatMap,
  resetUnreadMsgCountForInboxThread,
  roomJoined,
  resetBadgeCountController,
  setLoadingChats,
  initalInboxSetup,
  updateOnlineStatusOnInboxList,
  updateChatReadStatus,
  updateOptimisticMessage,
  increaseBadgeCountController,
  resetBadgeCountControllerCompletely,
  removeLocalChat,
  groupMemberRemove,
  groupMemberAdd,
  updateInboxListData,
  updateEditedMessage,
  updateEditedMessageInChatThread,
  updateChatStatus,
  updateGroupDetails,
  updateAdmins,
} from './chat.slice';
import { DEFAULT_ADMIN_ID } from '../../helpers/contants';
import { Chat, ChatPage, ContactDetails } from './chat.types';
import { notify } from '../../Utils/toastify';

// import { fetchNoticationList } from '../../redux/notificationSlice'

interface Props {
  children: ReactNode;
}
export const PAGE_LIMIT = 100;
export enum SocketEvents {
  // Connection Events
  CONNECTED = 'connected',
  DISCONNECT = 'disconnect',
  CONNECT_ERROR = 'connect_error',

  // Chat Events
  CHAT_LISTING = 'chatListing',
  MESSAGE_LISTING = 'messageListing',
  MESSAGE_READ = 'messageRead',
  RECEIVE_MESSAGE = 'receiveMessage',
  USER_ONLINE = 'user_online',
  ROOM_LEFT = 'roomLeft',
  USER_STATUS = 'user_status',
  ROOM_JOINED = 'roomJoined',
  PIN_MESSAGE = 'pinMessage',
  EDIT_MESSAGE = 'editMessage',
  BLOCK_USER = 'blockUser',
  UNBLOCK_USER = 'unblockUser',

  // Group Events
  RECEIVE_GROUP_MESSAGE = 'receiveGroupMessage',
  REMOVE_MEMBER = 'removeMember',
  ADD_MEMBER = 'addMember',
  GROUP_CREATED = 'groupCreated',

  // Order Events
  ORDER_RECEIVED = 'orderReceived',
  UPDATE_CART = 'updateCart',
  ORDER_PLACED = 'orderPlaced',

  // Other Events
  CHAT_REQUEST = 'chatRequest',
  // CHAT_REQUEST_RESPONSE = 'chatRequestResponse',
  BOOKING_STATUS_UPDATED = 'bookingStatusUpdated',

  // Emitters
  JOIN_ROOM = 'joinRoom',
  SEND_MESSAGE = 'sendMessage',
  // MESSAGE_LISTING = 'messageListing',
  GET_USER_STATUS = 'user_status',
  LEAVE_ROOM = 'leaveRoom',
  GROUP_MESSAGE = 'groupMessage',
  LEAVE_GROUP = 'leaveGroup',
  ADD_ADMIN = 'addAdmin',
  REMOVE_ADMIN = 'removeAdmin',
  REMOVE_FROM_GROUP = 'removeMember',
  GROUP_CREATED_EMIT = 'groupCreated',
  CHAT_REQUEST_EMIT = 'chatRequest',
  CREATE_GROUP = 'createGroup',
}
export type EmitEventFunction = (event: SocketEvents, params?: any) => void;
export interface NewchatReturn {
  newChat: Chat;
  chatId: string;
  newMessage: {
    chatId: string;
    messages: [];
  };
}
export interface SocketContextType {
  socket: Socket | null;
  isConnected: boolean;
  emitEvent: EmitEventFunction;
  connect: () => void;
  disconnect: () => void;
  retryConnection: () => void;
  listingFetched: any;
}
export interface ActiveChat {
  chatId: string;
  contactId?: string;
  newChat?: boolean;
}
export interface CreateNewChatProps {
  senderId: string;
  receiverId: string;
  contactDetails: ContactDetails;
  businessChatType: string;
  contactId: string;
  contactType: 'user' | 'vendor';
  chatType: 'ONE_TO_ONE';
}
const SocketContext = createContext<SocketContextType | undefined>(undefined);
const CHAT_SOCKET_URL: string = process.env.SOCKET_URL || '';
// const CHAT_SOCKET_URL: string = 'https://reelsocketqa.appskeeper.in';
// const CHAT_SOCKET_URL: string = 'https://pre-api.reeltrak.com/';

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (context === undefined) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};
export const SocketProvider: React.FC<Props> = ({ children }) => {
  const dispatch: AppDispatch = useDispatch();
  const backgroundJoinRooms = useRef<Set<string>>(new Set());
  const { mainInbox: chats } = useSelector((state: RootState) => state.chat);
  const { token, userData, status } = useAppSelector(
    (state: RootState) => state.auth,
  );
  const [socket, setSocket] = useState<Socket | null>(null);

  const { profileData } = useAppSelector((state: RootState) => state.common);
  // All refs for data accesssing
  const listingFetched = useRef<Record<string, boolean>>({});
  // An empty messageListing response carries no chatId, so remember which
  // chat the last request was for.
  const lastListingChatIdRef = useRef<string | null>(null);
  const listenerAdded = useRef<boolean>(false);
  const pagesRef = useRef<Record<string, ChatPage>>({});
  const [isConnected, setIsConnected] = useState(false);
  const currentUserId = profileData?._id || DEFAULT_ADMIN_ID;
  const [authFailed, setAuthFailed] = useState(false); // Track authentication failures
  const connectionAttempts = useRef(0); // Track connection attempts
  const maxConnectionAttempts = 3; // Maximum attempts before giving up

  // Store pending messageRead responses for messages not yet in Redux
  const pendingMessageReads = useRef<Map<string, any>>(new Map());

  // Helper function to apply pending messageRead responses
  const applyPendingMessageReads = (chatId: string, messages: any[]) => {
    // For each message, check if any pending read matches its real or temp ID
    messages.forEach(message => {
      // Try both real and temp IDs
      const possibleIds = [message._id];
      if (message.intermediateId) possibleIds.push(message.intermediateId);
      possibleIds.forEach(id => {
        const pendingKey = `${chatId}_${id}`;
        const pendingRead = pendingMessageReads.current.get(pendingKey);
        if (pendingRead) {
          // console.log('🔄 Applying pending messageRead for message:', id);
          // Merge receiverId with existing isRead array
          const existingIsRead = Array.isArray(message.isRead)
            ? message.isRead
            : [];
          const updatedIsRead = Array.from(
            new Set([...existingIsRead, pendingRead.receiverId]),
          );
          dispatch(
            updateMessageReadStatus({
              threadId: chatId,
              messageId: message._id,
              isRead: updatedIsRead,
            }),
          );
          // Remove from pending list
          pendingMessageReads.current.delete(pendingKey);
          // console.log('✅ Applied pending messageRead and removed from queue');
        }
      });
    });
    // Also, for any remaining pending reads, try to match by temp ID if message IDs have changed
    const allPending = Array.from(pendingMessageReads.current.entries());
    allPending.forEach(([pendingKey, pendingRead]) => {
      const [pendingChatId, pendingMsgId] = pendingKey.split('_');
      if (pendingChatId !== chatId) return;
      const found = messages.find(
        m => m.intermediateId === pendingMsgId || m._id === pendingMsgId,
      );
      if (found) {
        console.log(
          '🔄 Fuzzy match: Applying pending messageRead for message:',
          found._id,
        );
        const existingIsRead = Array.isArray(found.isRead) ? found.isRead : [];
        const updatedIsRead = Array.from(
          new Set([...existingIsRead, pendingRead.receiverId]),
        );
        dispatch(
          updateMessageReadStatus({
            threadId: chatId,
            messageId: found._id,
            isRead: updatedIsRead,
          }),
        );
        pendingMessageReads.current.delete(pendingKey);
        // console.log(
        //   '✅ Fuzzy match: Applied pending messageRead and removed from queue',
        // );
      }
    });
  };

  const connect = () => {
    if (!token) {
      console.log('🔌 No token available, skipping Socket.IO connection');
      return;
    }

    // Check if we've already failed authentication recently
    if (authFailed) {
      console.log(
        '🔌 Authentication previously failed, skipping connection attempt',
      );
      return;
    }

    // Check if we've exceeded max connection attempts
    if (connectionAttempts.current >= maxConnectionAttempts) {
      console.log(
        '🔌 Max connection attempts reached, stopping connection attempts',
      );
      setAuthFailed(true);
      return;
    }

    // Increment connection attempts
    connectionAttempts.current += 1;
    console.log(
      `🔌 Connection attempt ${connectionAttempts.current}/${maxConnectionAttempts}`,
    );

    // Validate token format (basic check)
    try {
      const tokenParts = token.split('.');
      if (tokenParts.length !== 3) {
        console.log('🔌 Invalid token format, skipping Socket.IO connection');
        setAuthFailed(true);
        return;
      }

      // Decode JWT payload to check expiration
      const payload = JSON.parse(atob(tokenParts[1]));
      const currentTime = Math.floor(Date.now() / 1000);

      if (payload.exp && payload.exp < currentTime) {
        console.log('🔌 Token expired, skipping Socket.IO connection');
        setAuthFailed(true);
        return;
      }

      // console.log('🔌 Token validation passed, proceeding with connection');
    } catch (_error) {
      // console.log('🔌 Token validation failed:', error);
      setAuthFailed(true);
      return;
    }

    // Prevent multiple connections
    if (socket?.connected) {
      // console.log('🔌 Socket already connected, skipping new connection');
      return;
    }

    console.log('🔌 Creating new socket connection...');
    const socketClient = io(CHAT_SOCKET_URL, {
      reconnection: true, // Enable automatic reconnection
      reconnectionAttempts: 5, // Try to reconnect 5 times
      reconnectionDelay: 1000, // Start with 1 second delay
      reconnectionDelayMax: 5000, // Max delay of 5 seconds
      timeout: 20000, // 20 second timeout
      transports: ['websocket', 'polling'],
      path: '',
      query: {
        accessToken: token,
        userType: 'USER',
        state: true,
        platform: '3', // Web platform
      },
    });

    setSocket(socketClient);
  };

  const disconnect = () => {
    if (socket) {
      console.log('Disconnecting socket...');
      socket.disconnect();
      setSocket(null);
      setIsConnected(false);
      // Reset refs
      listingFetched.current = {};
      pagesRef.current = {};
      listenerAdded.current = false;
    }
  };

  // Heartbeat mechanism to keep connection alive
  const heartbeatInterval = useRef<NodeJS.Timeout | null>(null);

  const startHeartbeat = () => {
    if (heartbeatInterval.current) {
      clearInterval(heartbeatInterval.current);
    }

    heartbeatInterval.current = setInterval(() => {
      if (socket?.connected) {
        // Send a ping to keep the connection alive
        // socket.emit('ping');
        // console.log('💓 Heartbeat sent');
      }
    }, 30000); // Send heartbeat every 30 seconds
  };

  const stopHeartbeat = () => {
    if (heartbeatInterval.current) {
      clearInterval(heartbeatInterval.current);
      heartbeatInterval.current = null;
    }
  };

  // Track last emission to prevent duplicates
  const lastEmission = useRef<{
    event: string;
    params: any;
    timestamp: number;
  }>({ event: '', params: null, timestamp: 0 });

  const emitEvent: EmitEventFunction = (event, params) => {
    if (socket?.connected) {
      const now = Date.now();
      const lastEmit = lastEmission.current;

      // Prevent duplicate emissions within 500ms for chatListing
      const debounceTime = event === SocketEvents.CHAT_LISTING ? 500 : 100;

      if (
        lastEmit.event === event &&
        JSON.stringify(lastEmit.params) === JSON.stringify(params) &&
        now - lastEmit.timestamp < debounceTime
      ) {
        // console.log(
        //   `Preventing duplicate emission: ${event} (within ${debounceTime}ms)`,
        // );
        return;
      }

      // console.log(`Emitting ${event}:`, params);
      // console.log('Socket connected:', socket?.connected);
      socket.emit(event, params);
      if (event === SocketEvents.MESSAGE_LISTING && params?.chatId) {
        lastListingChatIdRef.current = params.chatId;
      }

      // Update last emission
      lastEmission.current = { event, params, timestamp: now };
    } else {
      // console.log(`Emitting ${event} failed. Socket not connected`);
    }
  };

  // Socket event handlers based on React Native implementation
  const handleConnectionResponse = () => {
    // console.log("SOCKET : response of 'connected' : ", response);
    console.log(
      'handleConnectionResponse 1',
      listingFetched.current['chatListing'],
    );
    if (socket?.connected && !listingFetched.current['chatListing']) {
      // console.log('ConnectionResponse: Initial chat listing request');
      emitEvent(SocketEvents.CHAT_LISTING, {});
      listingFetched.current['chatListing'] = true;
    }
  };

  const handleDefaultConnect = () => {
    // console.log('🔌 Socket connected with server.', socket);
    if (socket?.connected) {
      setIsConnected(true);
      // Reset connection attempts on successful connection
      connectionAttempts.current = 0;
      setAuthFailed(false);
      // Start heartbeat to keep connection alive
      startHeartbeat();
    }
  };

  const handleDefaultDisconnect = () => {
    setIsConnected(false);
    // console.log('🔌 Socket disconnect with server.', socket);
    // Stop heartbeat when disconnected
    stopHeartbeat();
    // Reset listing flags on disconnect
    listingFetched.current = {};
  };

  const handleError = (errorDetail: any) => {
    setIsConnected(false);
    // Stop heartbeat on error
    stopHeartbeat();
    // console.log('🔌 Socket connect error with server:', errorDetail);

    // If it's an authentication error, disconnect and don't retry
    if (
      errorDetail?.message?.includes('unauthorized') ||
      errorDetail?.message?.includes('invalid token') ||
      errorDetail?.type === 'TransportError'
    ) {
      // console.log('🔌 Authentication error detected, disconnecting socket');
      disconnect();
      setAuthFailed(true); // Set authFailed flag
      // notify('Authentication failed. Please log in again.', 'error');
    }
  };

  const handleChatListing = (response: any) => {
    console.log('Response of get chat listing inbox: ', response);
    dispatch(resetBadgeCountControllerCompletely());
    const { data = [] } = response;
    if (data && data.length) {
      data?.forEach((item: any) => {
        if (item?.noOfUnreadMessage > 0) {
          // console.log('this is message item', item);
          dispatch(increaseBadgeCountController({ message: item }));
        }
      });

      // Remove duplicates from localChats
      const serverChatIds = new Set(response.data.map((chat: any) => chat._id));
      const { localChats } = store.getState().chat;
      localChats.forEach((localChat: any) => {
        if (serverChatIds.has(localChat._id)) {
          dispatch(removeLocalChat({ threadId: localChat._id }));
        }
      });
      dispatch(initalInboxSetup({ inbox: data }));
    } else {
      dispatch(resetBadgeCountController({ chatId: '' }));
    }
    dispatch(setLoadingChats(false)); // <-- set loading false after chats loaded
  };

  const handleMessageListing = (response: any) => {
    // console.log(
    //   ' handleMessageListing response====----=====>>>>>Response of message listing: ',
    //   response,
    //   roomJoined,
    //   // pinnedMessages
    // );

    if (response.statusCode === 200 && response.data && response.data.length) {
      // console.log(
      //   'handleMessageListing response====----=====>>>>>',
      //   response,
      //   response?.pinnedMessages,
      // );

      const { chatId } = response.data[0];
      listingFetched.current[chatId] = true;
      // listingFetched.current[chatId]=true

      // Check if this chat is PENDING
      const currentState = store.getState().chat;
      const chatDetails = currentState.mainInbox.find(
        (chat: any) => chat._id === chatId || chat.chatId === chatId,
      );
      if (chatDetails?.type != 'GROUP' && chatDetails) {
        dispatch(
          updateChatMap({
            chatId: chatId,
            chatData: {
              ...chatDetails,
              pinnedMessages: response?.pinnedMessages,
            },
          }),
        );
      }

      dispatch(
        updateInboxListData({
          chatId: chatId,
          data: { pinnedMessages: response?.pinnedMessages },
        }),
      );

      const isPendingChat = chatDetails?.status === 'PENDING';

      // Only emit messageRead for the last unread message from another user
      const unread = response.data.filter(
        (msg: any) =>
          msg.senderId !== currentUserId &&
          msg.type !== 'GROUP' &&
          !isPendingChat &&
          (!msg.isRead || msg.isRead.some((id: any) => id != msg.senderId)),
      );
      // console.log(unread);
      if (unread.length > 0) {
        const lastUnread = unread[unread.length - 1];
        emitEvent(SocketEvents.MESSAGE_READ, {
          messageId: lastUnread._id,
          chatId: chatId,
        });
      }

      const messages = response.data.map((messageObj: any) => {
        if (
          messageObj &&
          messageObj.hasOwnProperty('system') &&
          messageObj?.system &&
          messageObj?.type
        ) {
          delete messageObj['user'];
          return {
            ...messageObj,
            text: messageObj.message,
            user: { _id: '' },
          };
        }
        return {
          ...messageObj,
          user: {
            _id: messageObj.senderId,
          },
          text: messageObj.message,
        };
      });

      // Check if this is a pagination request (page > 1)
      const isPagination = response.pageNo > 1;

      if (isPagination) {
        // For pagination, add messages to the beginning of the thread
        dispatch(
          addMessagesToChatThreadInitially({
            message: messages ?? [],
            threadId: chatId,
            pageNo: response.pageNo,
          }),
        );
      } else {
        // For initial load, replace messages
        dispatch(
          addMessagesToChatThreadInitially({
            message: messages ?? [],
            threadId: chatId,
            pageNo: response.pageNo,
          }),
        );
      }

      // Emit custom event to notify ChatLayout that pagination/initial load is complete
      window.dispatchEvent(
        new CustomEvent('messagePaginationComplete', {
          detail: {
            chatId,
            // But by this if firstPage having 50 data it will give true but we need false
            // hasMore:response.data.length===50
            hasMore: response.totalPage > response.pageNo,
            pageNo: response.pageNo,
          },
        }),
      );

      // Apply any pending messageRead responses for these messages
      applyPendingMessageReads(chatId, messages);
    } else if (
      response.statusCode === 200 &&
      (!response.data || response.data.length === 0)
    ) {
      // No more messages to load — or none at all. The response has no
      // chatId, so fall back to the chat this listing was requested for.
      const chatId =
        response.chatId ||
        lastListingChatIdRef.current ||
        store.getState()?.chat?.activeChatId;
      if (chatId) listingFetched.current[chatId] = true;

      window.dispatchEvent(
        new CustomEvent('messagePaginationComplete', {
          detail: {
            chatId,
            hasMore: false,
            pageNo: response.pageNo,
          },
        }),
      );
    }
  };

  const handleMessageRead = (response: any) => {
    // console.log('🔍 handleMessageRead called with response:', response);

    // Server response structure: { messageId, chatId, receiverId, readTime, message }
    if (
      response &&
      response.messageId &&
      response.chatId &&
      response.receiverId
    ) {
      // console.log('✅ Valid messageRead response, updating Redux:', {
      //   threadId: response.chatId,
      //   messageId: response.messageId,
      //   receiverId: response.receiverId,
      //   readTime: response.readTime,
      // });

      // Get current message from Redux to merge with existing isRead array
      const currentState = store.getState().chat;
      const messages = currentState.chatMessages[response.chatId] || [];
      const message = messages.find((m: any) => m._id === response.messageId);

      console.log('🔍 Message lookup debug:', {
        responseChatId: response.chatId,
        responseMessageId: response.messageId,
        availableChatIds: Object.keys(currentState.chatMessages),
        messagesInChat: messages.length,
        messageIdsInChat: messages.map((m: any) => m._id),
        foundMessage: !!message,
      });

      if (message) {
        const currentState = store.getState().chat;
        const messages = currentState.chatMessages[response.chatId] || [];

        // Create a set of updated message updates to dispatch
        const updates = [];

        messages.forEach((msg: any) => {
          const messageCreatedAt = new Date(msg.createdAt).getTime();
          const readTime = new Date(response.readTime).getTime();

          // If message was created before or at the readTime, mark as read
          if (messageCreatedAt <= readTime) {
            // Merge receiverId with existing isRead array
            const existingIsRead = Array.isArray(msg.isRead) ? msg.isRead : [];
            if (!existingIsRead.includes(response.receiverId)) {
              const updatedIsRead = Array.from(
                new Set([...existingIsRead, response.receiverId]),
              );

              updates.push({
                threadId: response.chatId,
                messageId: msg._id,
                isRead: updatedIsRead,
                readTime: response.readTime,
              });
            }
          }
        });

        // Dispatch all updates (can be batched in your slice if possible)
        updates.forEach(update => {
          dispatch(updateMessageReadStatus(update));
        });
        // dispatch(
        //   updateMessageReadStatus({
        //     threadId: response.chatId,
        //     messageId: response.messageId,
        //     isRead: updatedIsRead,
        //   }),
        // );
        // console.log(
        //   '✅ messageRead Redux update dispatched with merged isRead:',
        //   updatedIsRead,
        // );
      } else {
        // console.log(
        //   '❌ Message not found in Redux state for messageRead update',
        // );
        // console.log(
        //   '🔍 Available messages in all chats:',
        //   Object.keys(currentState.chatMessages).map(chatId => ({
        //     chatId,
        //     messageCount: currentState.chatMessages[chatId]?.length || 0,
        //     messageIds:
        //       currentState.chatMessages[chatId]?.map((m: any) => m._id) || [],
        //   })),
        // );

        // console.log(
        //   '🔍 Recent messages in target chat:',
        //   recentMessages.map((m: any) => ({
        //     _id: m._id,
        //     senderId: m.senderId,
        //     message: m.message?.substring(0, 30) + '...',
        //     createdAt: m.createdAt,
        //     isRead: m.isRead,
        //   })),
        // );

        // console.log(
        //   '🔍 Messages from the same sender (receiverId):',
        //   messagesFromSameSender.map((m: any) => ({
        //     _id: m._id,
        //     message: m.message?.substring(0, 30) + '...',
        //     isRead: m.isRead,
        //   })),
        // );

        // Fallback: Log that we couldn't update the specific message
        // console.log(
        //   '🔄 Fallback: Could not update specific message, but read receipt was received',
        // );
        // console.log('💡 This might be because:');
        // console.log('   - Message was sent from a different session/device');
        // console.log('   - Message was not yet loaded in this session');
        // console.log('   - Message ID mismatch between server and client');

        // Store this read response for later application when message is loaded
        const pendingKey = `${response.chatId}_${response.messageId}`;
        pendingMessageReads.current.set(pendingKey, {
          messageId: response.messageId,
          chatId: response.chatId,
          receiverId: response.receiverId,
          readTime: response.readTime,
        });
        // console.log(
        //   '📝 Stored pending messageRead for later application:',
        //   pendingKey,
        // );

        // FALLBACK: Update the most recent message from the same sender to show double tick
        const recentMessagesFromSender = messages.filter(
          (m: any) => m.senderId === response.receiverId,
        );

        if (recentMessagesFromSender.length > 0) {
          // Get the most recent message from the same sender
          const mostRecentMessage =
            recentMessagesFromSender[recentMessagesFromSender.length - 1];
          // console.log(
          //   '🔄 FALLBACK: Updating most recent message from same sender:',
          //   {
          //     messageId: mostRecentMessage._id,
          //     senderId: mostRecentMessage.senderId,
          //     message: mostRecentMessage.message?.substring(0, 30) + '...',
          //   },
          // );

          // Update the isRead array for this message
          const existingIsRead = Array.isArray(mostRecentMessage.isRead)
            ? mostRecentMessage.isRead
            : [];
          const updatedIsRead = Array.from(
            new Set([...existingIsRead, response.receiverId]),
          );

          dispatch(
            updateMessageReadStatus({
              threadId: response.chatId,
              messageId: mostRecentMessage._id,
              isRead: updatedIsRead,
            }),
          );
          // console.log(
          //   '✅ FALLBACK: Updated most recent message with read status:',
          //   updatedIsRead,
          // );
        } else {
          // console.log(
          //   '❌ No messages found from the same sender for fallback update',
          // );
        }

        // ULTIMATE FALLBACK: Update chat-level read status to ensure double ticks show
        // console.log('🔄 ULTIMATE FALLBACK: Updating chat-level read status');
        dispatch(
          updateChatReadStatus({
            threadId: response.chatId,
            lastReadTime: response.readTime,
            readByUserId: response.receiverId,
          }),
        );
        console.log('✅ ULTIMATE FALLBACK: Chat-level read status updated');
      }
    } else {
      console.log('❌ Invalid messageRead response:', {
        hasResponse: !!response,
        hasMessageId: !!response?.messageId,
        hasChatId: !!response?.chatId,
        hasReceiverId: !!response?.receiverId,
        response,
      });
    }
  };

  const handleReceiveMessage = (response: any) => {
    // console.log('Response of receive message: ', response);
    if (response.data) {
      console.log('Response of receive message1: ', response.data);

      // Extract the actual message data from lastMessageDetail
      const messageData = response.data?.lastMessageDetail;
      const chatId = response.data._id;
      const messageId = messageData?._id;
      const senderId = messageData?.senderId;
      const intermediateId = messageData?.intermediateId;
      console.log('New message details:', {
        chatId,
        messageId,
        senderId,
        messageData,
      });

      // Check if this is our own message (optimistic update)
      const isOwnMessage = senderId === currentUserId;

      if (isOwnMessage) {
        // This is our own message - update the optimistic message
        // console.log('Updating optimistic message with real ID:', messageId);

        // Find the optimistic message by checking recent messages
        const messages = store.getState().chat.chatMessages[chatId] || [];
        // console.log('All messages in chat:', messages);

        const optimisticMessage = messages.find(
          (msg: any) =>
            (msg.intermediateId == intermediateId &&
              msg.senderId === currentUserId) ||
            (msg.status === 'UPLOADING' && msg.senderId === currentUserId),
        );

        // console.log('Found optimistic message:', optimisticMessage);

        // Helper to get valid ISO date
        const getValidISODate = (dateVal: any) => {
          if (!dateVal) return new Date().toISOString();
          if (typeof dateVal === 'number')
            return new Date(dateVal).toISOString();
          // If string, try to parse
          const d = new Date(dateVal);
          return isNaN(d.getTime())
            ? new Date().toISOString()
            : d.toISOString();
        };
        const createdAt = getValidISODate(
          messageData?.created || messageData?.createdAt,
        );

        if (optimisticMessage) {
          console.log('Found optimistic message to update:', optimisticMessage);

          dispatch(
            updateOptimisticMessage({
              threadId: chatId,
              tempId: optimisticMessage.intermediateId || optimisticMessage._id,
              realMessage: {
                ...messageData,
                createdAt,
                user: {
                  _id: senderId,
                },
                text: messageData?.message,
                status: 'ACTIVE', // Ensure status is updated to ACTIVE
              },
            }),
          );
        } else {
          console.log('No optimistic message found, adding as new message');
          // Fallback: add the message normally
          const newMessage = {
            ...messageData,
            createdAt,
            user: {
              _id: senderId,
            },
            text: messageData?.message,
          };

          dispatch(
            addMessagesToChatThread({
              threadId: chatId,
              message: [newMessage],
            }),
          );

          // Apply any pending messageRead responses for this new message
          applyPendingMessageReads(chatId, [newMessage]);
        }
      } else {
        // This is a message from someone else - add it normally
        console.log('Adding received message from other user');
        // Helper to get valid ISO date
        const getValidISODate = (dateVal: any) => {
          if (!dateVal) return new Date().toISOString();
          if (typeof dateVal === 'number')
            return new Date(dateVal).toISOString();
          // If string, try to parse
          const d = new Date(dateVal);
          return isNaN(d.getTime())
            ? new Date().toISOString()
            : d.toISOString();
        };
        const createdAt = getValidISODate(
          messageData?.created || messageData?.createdAt,
        );
        const newMessage = {
          ...messageData,
          createdAt,
          user: {
            _id: senderId,
          },
          text: messageData?.message,
        };

        dispatch(
          addMessagesToChatThread({
            threadId: chatId,
            message: [newMessage],
          }),
        );

        // Apply any pending messageRead responses for this new message
        applyPendingMessageReads(chatId, [newMessage]);
      }

      // Update inbox list

      // if (senderId !== currentUserId) {
      dispatch(
        updateInboxList({
          threadId: response.data.type?.toLowerCase().includes('group')
            ? response.data?.chatId
            : response.data?._id,
          newData: response.data,
          currentUserId: currentUserId,
        }),
      );
      // }

      // If user is actively viewing this chat, automatically mark message as read
      const currentState = store.getState().chat;
      const isCurrentlyActiveChat = currentState.activeChatId === chatId;

      // Get the chat details to check status
      const chatDetails = currentState.mainInbox.find(
        (chat: any) => chat._id === chatId || chat.chatId === chatId,
      );

      if (!chatDetails) {
        // console.log('No chat details found for ID:', chatId);

        emitEvent(SocketEvents.CHAT_LISTING, {});
        // return;
      }
      console.log('chatDetails=>>>>>1', chatDetails);

      const isPendingChat = chatDetails?.status === 'PENDING';

      if (
        isCurrentlyActiveChat &&
        !isOwnMessage &&
        messageId &&
        !isPendingChat
      ) {
        console.log(
          '🔍 Auto-marking message as read (user is actively viewing chat):',
          {
            messageId,
            chatId,
            activeChatId: currentState.activeChatId,
            chatStatus: chatDetails?.status,
          },
        );

        // Emit messageRead event to mark as read
        emitEvent(SocketEvents.MESSAGE_READ, {
          messageId: messageId,
          chatId: chatId,
        });
      }
    }
  };

  const handleOnlineOffline = (response: any) => {
    // console.log("SOCKET : response of 'user_online' : ", response);
    dispatch(updateOnlineStatusOnInboxList({ response }));
  };

  const handleRoomLeft = (response: any) => {
    console.log("SOCKET : response of 'roomLeft' : ", response);
    // Reset message listing for the left room
    if (response?.data?.chatId) {
      const leftRoomId = response.data.chatId;
      console.log(`Successfully left room: ${leftRoomId}`);
      // Clear any cached data for this room if needed
      delete listingFetched.current[leftRoomId];
    }
  };

  const handleUserStatus = (response: any) => {
    console.log("SOCKET : response of 'user_status' : ", response);
  };

  const handleRoomJoined = (response: any) => {
    console.log("SOCKET : response of 'roomJoined' : ", response);

    if (response?.data?.status === false) {
      // Not a member of this room (e.g. removed from group) — no chatId is
      // returned in this case, so there's nothing further to join/fetch.
      return;
    }

    const { chatId } = response?.data;

    // Silent background join (e.g. receiver's new group) — skip all UI side effects
    if (backgroundJoinRooms.current.has(chatId)) {
      backgroundJoinRooms.current.delete(chatId);
      return;
    }

    if (chatId) {
      dispatch(resetUnreadMsgCountForInboxThread({ threadId: chatId }));
      dispatch(roomJoined({ roomId: chatId }));
      dispatch(resetBadgeCountController({ chatId: chatId }));
    }

    // Handle group member data like React Native
    if (response?.data?.topMembers && response?.data?.totalMembers) {
      const { topMembers = [], totalMembers = 0 } = response.data;

      console.log('chatId in SocketContext  =>', chatId);

      const chatToUpdate =
        store
          .getState()
          .chat.mainInbox.find(
            (chat: any) => chat._id === chatId || chat.chatId === chatId,
          ) ||
        store
          .getState()
          .chat.localChats.find(
            (chat: any) => chat._id === chatId || chat.chatId === chatId,
          );

      if (chatToUpdate) {
        dispatch(
          updateChatMap({
            chatId: chatId,
            chatData: {
              ...chatToUpdate,
              memberDetails: {
                topMembers,
                totalMembers,
                memberLeft: response.data.memberLeft || [],
              },
            },
          }),
        );
      }
    }

    const messagesForChat = store.getState().chat.chatMessages?.[chatId] || [];

    const isPendingChat =
      store
        .getState()
        .chat.mainInbox.find(
          (chat: any) => chat._id === chatId || chat.chatId === chatId,
        )?.status === 'PENDING';

    const isLocalChat = store
      .getState()
      .chat.localChats.find(
        (chat: any) => chat._id === chatId || chat.chatId === chatId,
      )?.isLocalChatStart;
    if (isLocalChat) {
      listingFetched.current[chatId] = true;
    }
    const unread = messagesForChat.filter(
      (msg: any) =>
        msg.senderId !== currentUserId &&
        msg.type !== 'GROUP' &&
        !isPendingChat &&
        (!msg.isRead ||
          !msg.isRead.some((readerId: string) => readerId !== msg.senderId)),
    );

    if (unread.length > 0) {
      const lastUnread = unread[unread.length - 1];
      emitEvent(SocketEvents.MESSAGE_READ, {
        messageId: lastUnread._id,
        chatId,
      });
    }

    // Request messages for this chat only if not already fetched or no messages present
    if (
      (!listingFetched.current[chatId] || messagesForChat.length === 0) &&
      !isLocalChat
    ) {
      const params = {
        chatId: chatId || store.getState()?.chat?.activeChatId,
        limit: 50,
        pageNo: 1,
      };

      emitEvent(SocketEvents.MESSAGE_LISTING, params);
    } else {
      console.log(`Messages already fetched for chat: ${chatId}`);
    }
  };

  const handleReceiveGroupMessage = (response: any) => {
    console.log('Response of receive group message: ', response);
    console.log('Group message data:', response.data);
    if (response.data) {
      const chatId = response.data.chatId;
      const senderId = response.data.senderId;
      const messageId = response.data._id;
      const intermediateId = response.data.intermediateId;

      // Check for optimistic message
      const messages = store.getState().chat.chatMessages[chatId] || [];

      const optimisticMessageIndex = messages.findIndex(
        (msg: any) =>
          msg.intermediateId === intermediateId &&
          intermediateId &&
          msg.senderId === senderId,
      );

      console.log(messages, 'Group message data:');

      const newMessage = {
        ...response.data,
        user: { _id: senderId },
        text: response.data.message,
      };

      console.log('Group message data:', optimisticMessageIndex);

      if (optimisticMessageIndex !== -1) {
        // Replace optimistic message with real one
        dispatch(
          updateOptimisticMessage({
            threadId: chatId,
            tempId: messages[optimisticMessageIndex].intermediateId,
            realMessage: newMessage,
          }),
        );
      } else {
        // Add as new message
        dispatch(
          addMessagesToChatThread({
            threadId: chatId,
            message: [newMessage],
          }),
        );
      }

      // // Update inbox list
      dispatch(
        updateInboxList({
          threadId: chatId,
          newData: {
            ...response.data,
            type: 'GROUP', // Ensure group code path in updateInboxList (message objects lack this)
            chatId: chatId,
            _id: chatId, // Keep the group's _id, not the message's _id
          },
          currentUserId: currentUserId,
        }),
      );

      // If user is actively viewing this group chat, automatically mark message as read
      const currentState = store.getState().chat;
      const isCurrentlyActiveChat = currentState.activeChatId === chatId;
      const isOwnMessage = senderId === currentUserId;

      if (isCurrentlyActiveChat && !isOwnMessage && messageId) {
        console.log(
          '🔍 Auto-marking group message as read (user is actively viewing chat):',
          {
            messageId,
            chatId,
            activeChatId: currentState.activeChatId,
          },
        );

        // Emit messageRead event to mark as read
        emitEvent(SocketEvents.MESSAGE_READ, {
          messageId: messageId,
          chatId: chatId,
        });
      }
    }
  };

  const handleRemoveMember = (response: any) => {
    console.log('Response of remove member =>>>>>>>>>>: ', response);

    if (response.data) {
      const {
        memberLeft,
        message: { chatId },
      } = response.data;

      console.log('🔍 handleRemoveMember: ', {
        chatId,
        memberLeft,
      });

      if (memberLeft) {
        dispatch(
          groupMemberRemove({ chatId: chatId, leftMembers: memberLeft }),
        );
      }
    }
  };

  const handleOrderPlaced = (response: any) => {
    console.log('Response of order placed: ', response);
  };

  const handleGroupCreated = (response: any) => {
    console.log('Response of group created: ', response);
    if (!response.data) return;

    const groupId = response?.data?._id;
    const state = store.getState().chat;

    // Skip if creator already added this group to mainInbox in CreateGroupModal
    const alreadyInInbox =
      state.mainInbox.find(
        (c: any) => c._id === groupId || c.chatId === groupId,
      ) ||
      state.localChats.find(
        (c: any) => c._id === groupId || c.chatId === groupId,
      );
    if (alreadyInInbox) return;

    const newGroupChat = {
      _id: groupId,
      threadId: groupId,
      chatId: groupId,
      type: 'GROUP' as const,
      businessChatType: response?.data?.businessChatType || 'entity_group',
      ...response?.data,
    };

    dispatch(initalInboxSetup({ inbox: [newGroupChat, ...state.mainInbox] }));
    dispatch(updateChatMap({ chatId: groupId, chatData: newGroupChat }));
    dispatch(
      updateGroupDetails({
        chatId: groupId,
        groupName: response?.data?.groupName,
        groupImage: response?.data?.groupImage,
        members: response?.data?.members,
      }),
    );
    emitEvent(SocketEvents.JOIN_ROOM, {
      chatId: groupId,
      businessChatType: response?.data?.businessChatType || 'entity_group',
    });
    backgroundJoinRooms.current.add(groupId);

    console.log('✅ Group created and added to chat list:', response.data);
  };

  const handleAddMember = (response: any) => {
    console.log('Response of add member =>>>>>>>: ', response);
    if (response.data) {
      const { _id, members } = response.data;
      dispatch(groupMemberAdd({ chatId: _id, members: members }));
    }
  };

  const handleLeaveGroup = (response: any) => {
    console.log('Response of leave group: ', response);
    if (response.data) {
      const { chatId, success } = response.data;

      console.log('🔍 handleLeaveGroup: ', response.data);

      if (success) {
        // Remove group from chat listing
        dispatch(removeGroupFromChatList({ groupId: chatId }));

        // Clear active chat if it's the group we just left
        const currentState = store.getState().chat;
        if (currentState.activeChatId === chatId) {
          dispatch(setActiveChat(''));
        }

        // Show success notification
        notify('Successfully left the group', 'success');

        // Refresh chat listing to ensure UI is updated
        // emitEvent(SocketEvents.CHAT_LISTING, {});
      } else {
        // notify('Failed to leave group. Please try again.', 'error');
      }
    }
  };

  const handleAddAdmin = (response: any) => {
    console.log('Response of add admin: ', response);
    if (response.data) {
      const { chatId, admins } = response.data;
      dispatch(updateAdmins({ chatId, admins }));
    }
  };

  const handleRemoveAdmin = (response: any) => {
    console.log('Response of remove admin: ', response);
    if (response.data) {
      const { success } = response.data;

      if (success) {
        notify('Admin demoted successfully', 'success');
        // Refresh group details to show updated admin status
        // This would typically trigger a reload of group details
      } else {
        notify('Failed to demote admin. Please try again.', 'error');
      }
    }
  };

  const handleChatRequest = (response: any) => {
    console.log('Response of chat request: ', response);

    // Handle chat request notification
    if (response.data) {
      const requestData = response.data;
      const contactName =
        requestData.contactName ||
        `${requestData.firstName || ''} ${requestData.lastName || ''}`.trim() ||
        'Unknown User';

      // Show notification for chat request
      notify(`${contactName} wants to start a chat with you`, 'info');
    }

    // Refresh chat listing if needed
    if (response.data && !listingFetched.current['chatRequest']) {
      console.log('ChatRequest: Refreshing chat listing');
      emitEvent(SocketEvents.CHAT_LISTING, {});
      listingFetched.current['chatRequest'] = true;
      // Reset after 5 seconds to allow future requests
      setTimeout(() => {
        listingFetched.current['chatRequest'] = false;
      }, 5000);
    }
  };

  const handleChatRequestResponse = (response: any) => {
    console.log('Response of chat request response: ', response);
    if (response.data) {
      const requestData = response.data;
      const contactName =
        requestData.contactName ||
        `${requestData.firstName || ''} ${requestData.lastName || ''}`.trim() ||
        'Unknown User';

      if (requestData.status === 'accepted') {
        notify(`${contactName} accepted your chat request`, 'success');
      } else if (requestData.status === 'rejected') {
        notify(`${contactName} rejected your chat request`, 'error');
      }
    }
  };

  const handleBookingRequest = (response: any) => {
    console.log('Response of booking request: ', response);
  };

  const handleUpdateCart = (response: any) => {
    console.log('Response of update cart: ', response);
  };

  const handleSyncMessage = (response: any) => {
    console.log('🔄 Response of sync message: ', response);

    if (!response?.data) {
      console.log('❌ Invalid sync message response - no data');
      return null;
    }

    const syncData = response.data;
    console.log('📱 Sync message data:', syncData);

    return handleSyncMessageData(syncData);
  };

  const handleSyncMessageData = (syncData: any) => {
    console.log('📱 Processing sync message data:', syncData);

    // Handle your specific data structure
    const messageData = syncData.lastMessageDetail;
    const chatId = syncData._id; // Use the chat _id as chatId
    const messageId = messageData?._id;
    const senderId = messageData?.senderId;
    const intermediateId = messageData?.intermediateId;

    if (!chatId || !messageData || !messageId) {
      console.log('❌ Invalid sync message data - missing required fields');
      return null;
    }

    // Check if message already exists in chat messages
    const currentState = store.getState().chat;
    const existingMessages = currentState.chatMessages[chatId] || [];

    // Check if we have an optimistic message to update
    const optimisticMessageIndex = existingMessages.findIndex(
      (msg: any) =>
        msg.intermediateId == intermediateId && msg.senderId === senderId,
    );
    console.log(optimisticMessageIndex, 'sync Data');
    if (optimisticMessageIndex !== -1) {
      // console.log('optimisticMessageIndex found:', optimisticMessageIndex);
      const hasOptimisticMessage = optimisticMessageIndex !== -1;

      // Check if this message is newer than the current last message in inbox
      const chatInInbox = currentState.mainInbox.find(
        (chat: any) => chat._id === chatId || chat.chatId === chatId,
      );

      if (!chatInInbox) {
        console.log('chatInInbox not found');
        emitEvent(SocketEvents.CHAT_LISTING, {});
        return;
      }

      const isOptimisticMessage = hasOptimisticMessage;

      const shouldUpdateInbox =
        !chatInInbox ||
        !chatInInbox.lastMessageDetail?._id ||
        !isOptimisticMessage;
      console.log(currentUserId);
      console.log('📱 Sync message details:', {
        chatId,
        messageId,
        senderId,
        hasOptimisticMessage,
        shouldUpdateInbox,
        currentLastMessageId: chatInInbox?.lastMessageDetail?._id,
        newMessageCreated: messageData.created,
        currentLastMessageCreated: chatInInbox?.lastMsgCreated,
      });

      // Helper to get valid ISO date
      const getValidISODate = (dateVal: any) => {
        if (!dateVal) return new Date().toISOString();
        if (typeof dateVal === 'number') return new Date(dateVal).toISOString();
        const d = new Date(dateVal);
        return isNaN(d.getTime()) ? new Date().toISOString() : d.toISOString();
      };

      const createdAt = getValidISODate(
        messageData?.created || messageData?.createdAt,
      );

      const newMessage = {
        ...messageData,
        createdAt,
        user: {
          _id: senderId,
        },
        text: messageData?.message,
        status: 'ACTIVE', // Ensure status is ACTIVE for synced messages
      };

      // Update optimistic message or add new message
      if (hasOptimisticMessage) {
        console.log(
          '🔄 Updating optimistic message with real data:',
          messageId,
        );

        // Update the optimistic message with real message data
        dispatch(
          updateOptimisticMessage({
            threadId: chatId,
            tempId: existingMessages[optimisticMessageIndex]._id,
            realMessage: newMessage,
          }),
        );
      } else {
        console.log('📱 Adding new message to chat thread', newMessage);

        // Add new message to chat thread
        dispatch(
          addMessagesToChatThread({
            threadId: chatId,
            message: [newMessage],
          }),
        );
      }
      // Apply any pending messageRead responses for this new message
      applyPendingMessageReads(chatId, [newMessage]);

      // Update inbox list only if message is newer
      if (shouldUpdateInbox) {
        console.log('✅ Updating inbox with new message');
        dispatch(
          updateOptimisticMessage({
            threadId: chatId,
            tempId: existingMessages[optimisticMessageIndex]._id,
            realMessage: newMessage,
          }),
        );
        // dispatch(
        //   updateInboxList({
        //     threadId: chatId,
        //     newData: {
        //       ...syncData, // Use the entire syncData as it contains all chat info
        //       lastMessageDetail: messageData,
        //       lastMsgCreated: messageData?.created || Date.now(),
        //     },
        //     currentUserId: currentUserId,
        //   }),
        // );
      } else {
        console.log(
          '⏭️ Skipping inbox update - message is not newer than current last message',
        );
      }

      // If user is actively viewing this chat, automatically mark message as read
      const isCurrentlyActiveChat = currentState.activeChatId === chatId;
      const isPendingChat = chatInInbox?.status === 'PENDING';
      const isOwnMessage = senderId === currentUserId;

      if (
        isCurrentlyActiveChat &&
        !isOwnMessage &&
        messageId &&
        !isPendingChat
      ) {
        console.log(
          '🔍 Auto-marking synced message as read (user is actively viewing chat):',
          {
            messageId,
            chatId,
            activeChatId: currentState.activeChatId,
            chatStatus: chatInInbox?.status,
            isOwnMessage,
          },
        );

        // Emit messageRead event to mark as read
        // emitEvent(SocketEvents.MESSAGE_READ, {
        //   messageId: messageId,
        //   chatId: chatId,
        // });
      }

      console.log('✅ Sync message processed successfully');
      // return { messageAdded: true, inboxUpdated: shouldUpdateInbox };

      return;
    } else {
      // Helper to get valid ISO date
      const getValidISODate = (dateVal: any) => {
        if (!dateVal) return new Date().toISOString();
        if (typeof dateVal === 'number') return new Date(dateVal).toISOString();
        const d = new Date(dateVal);
        return isNaN(d.getTime()) ? new Date().toISOString() : d.toISOString();
      };

      const createdAt = getValidISODate(
        messageData?.created || messageData?.createdAt,
      );
      const newMessage = {
        ...messageData,
        createdAt,
        user: {
          _id: senderId,
        },
        text: messageData?.message,
        status: 'ACTIVE', // Ensure status is ACTIVE for synced messages
      };
      dispatch(
        addMessagesToChatThread({
          threadId: chatId,
          message: [newMessage],
        }),
      );
    }
  };

  const handleTopMembers = (data: any) => {
    console.log(data);
    const { chatId, topMembers = [], totalMembers = 0 } = data.data;

    const chatToUpdate =
      store
        .getState()
        .chat.mainInbox.find(
          (chat: any) => chat._id === chatId || chat.chatId === chatId,
        ) ||
      store
        .getState()
        .chat.localChats.find(
          (chat: any) => chat._id === chatId || chat.chatId === chatId,
        );

    dispatch(
      updateChatMap({
        chatId: chatId,
        chatData: {
          ...chatToUpdate,
          // members: [...chatToUpdate?.members ?? [],...topMembers.map((member: any) => member._id || member.userId)],
          memberDetails: {
            topMembers,
            totalMembers,
          },
        },
      }),
    );
  };

  const handlePinMessage = (payload: any) => {
    const { pinStatus, message } = payload?.data;

    console.log('PIN payload', payload, pinStatus, message, chats);

    if (pinStatus === 'PINNED') {
      dispatch(
        updateInboxListData({
          chatId: message?.chatId,
          data: {
            syncMessage: true,
            message,
          },
        }),
      );

      notify('Message pinned successfully', 'success');
    } else {
      dispatch(
        updateInboxListData({
          chatId: message?.chatId,
          data: {
            upPin: true,
            message,
          },
        }),
      );
      notify('Message unpinned successfully', 'success');
    }
  };

  const handleEditMessage = (payload: any) => {
    console.log('EditPayload', payload);
    const { data } = payload;

    dispatch(
      updateEditedMessageInChatThread({
        threadId: data?.chatId,
        messageId: data?._id,
        message: data?.message,
        isEdited: true,
      }),
    );

    dispatch(updateEditedMessage(null));
  };

  // const handleMessageSent = (response: any) => {
  //   const responseData = response?.data;
  //   console.log('Message sent Response back', responseData);

  //   dispatch(updateNewMessageWithId(responseData));
  // };

  const handleBlockUser = (payload: any) => {
    // console.log('BlockUser payload', payload);
    const { data } = payload;
    // notify('User Blocked Successfully', 'success');

    dispatch(
      updateChatStatus({
        chatId: data?.chatId,
        status: 'BLOCKED',
        data: {
          status: 'BLOCKED',
          blockedBy: data?.blockedBy,
        },
      }),
    );
  };

  const handleUnblockUser = (payload: any) => {
    // console.log('UnblockUser payload', payload);
    const { data } = payload;
    // notify('User Unblocked Successfully', 'success');

    dispatch(
      updateChatStatus({
        chatId: data?.chatId,
        status: 'ACTIVE',
      }),
    );
  };

  // Add socket listeners
  useEffect(() => {
    if (socket && !listenerAdded.current) {
      // console.log('Adding socket event listeners...');
      listenerAdded.current = true;

      // Connection listeners
      socket.on('connect', handleDefaultConnect);
      socket.on('disconnect', handleDefaultDisconnect);
      socket.on('connect_error', handleError);
      socket.on('connected', handleConnectionResponse);
      socket.on('pong', () => {
        // console.log('💓 Heartbeat response received');
      });

      // Chat listeners
      socket.on('chatListing', handleChatListing);
      socket.on('messageListing', handleMessageListing);
      // socket.on('sendMessage', handleMessageSent);
      socket.on('messageRead', handleMessageRead);
      socket.on('receiveMessage', handleReceiveMessage);
      socket.on('user_online', handleOnlineOffline);
      socket.on('roomLeft', handleRoomLeft);
      socket.on('user_status', handleUserStatus);
      socket.on('roomJoined', handleRoomJoined);
      socket.on('syncMessage', handleSyncMessage);
      socket.on('pinMessage', handlePinMessage);
      socket.on('editMessage', handleEditMessage);
      socket.on('blockUser', handleBlockUser);
      socket.on('unblockUser', handleUnblockUser);

      // Group listeners
      socket.on('receiveGroupMessage', handleReceiveGroupMessage);
      socket.on('removeMember', handleRemoveMember);
      socket.on('addMember', handleAddMember);
      socket.on('groupCreated', handleGroupCreated);
      socket.on('leaveGroup', handleLeaveGroup);
      socket.on('addAdmin', handleAddAdmin);
      socket.on('removeAdmin', handleRemoveAdmin);
      socket.on('topMembers', handleTopMembers);
      // Order listeners
      socket.on('orderReceived', handleOrderPlaced);
      socket.on('updateCart', handleUpdateCart);
      socket.on('orderPlaced', handleOrderPlaced);

      // Other listeners
      socket.on('chatRequest', handleChatRequest);
      socket.on('chatRequestResponse', handleChatRequestResponse);
      socket.on('bookingStatusUpdated', handleBookingRequest);

      return () => {
        // Clean up all listeners when component unmounts or socket changes
        // console.log('Cleaning up socket event listeners...');
        listenerAdded.current = false;
        socket.off('connect');
        socket.off('disconnect');
        socket.off('connect_error');
        socket.off('connected');
        socket.off('pong');
        socket.off('chatListing');
        socket.off('messageListing');
        socket.off('sendMessage');
        socket.off('messageRead');
        socket.off('receiveMessage');
        socket.off('syncMessage');
        socket.off('user_online');
        socket.off('roomLeft');
        socket.off('user_status');
        socket.off('roomJoined');
        socket.off('receiveGroupMessage');
        socket.off('removeMember');
        socket.off('addMember');
        socket.off('groupCreated');
        socket.off('leaveGroup');
        socket.off('addAdmin');
        socket.off('removeAdmin');
        socket.off('orderReceived');
        socket.off('updateCart');
        socket.off('orderPlaced');
        socket.off('chatRequest');
        socket.off('chatRequestResponse');
        socket.off('bookingStatusUpdated');
        socket.off('pinMessage');
        socket.off('editMessage');
      };
    }
  }, [socket]); // Only depend on socket, not other values that change frequently

  // Load chat listing on mount - only once when socket connects
  const chatListingFetched = useRef(false);
  useEffect(() => {
    if (socket?.connected && !chatListingFetched.current) {
      // console.log('ChatLayout: Loading chat listing on mount');
      dispatch(setLoadingChats(true)); // <-- set loading true before fetching
      emitEvent(SocketEvents.CHAT_LISTING, {});
      chatListingFetched.current = true;
    }
  }, [socket?.connected]);

  // Manual retry function for socket connection
  const retryConnection = () => {
    // console.log('🔌 Manual retry requested');
    setAuthFailed(false); // Reset auth failed flag
    connectionAttempts.current = 0; // Reset connection attempts
    disconnect(); // Disconnect any existing connection
    setTimeout(() => {
      if (token && status && userData) {
        connect();
      }
    }, 1000); // Wait 1 second before retrying
  };

  // Connect when access token is available
  useEffect(() => {
    // Reset authFailed flag and connection attempts when user is properly authenticated
    if (token && status && userData) {
      setAuthFailed(false);
      connectionAttempts.current = 0; // Reset connection attempts
    }

    if (token && status && userData && !authFailed) {
      // console.log('🔌 Attempting to connect Socket.IO...');
      // console.log('🔌 Auth status:', {
      //   token: !!token,
      //   status,
      //   hasUserData: !!userData,
      // });
      connect();
    } else {
      // console.log('🔌 Socket.IO connection skipped - not authenticated:', {
      //   hasToken: !!token,
      //   status,
      //   hasUserData: !!userData,
      //   authFailed,
      // });
      // Disconnect if we have a socket but user is not authenticated
      if (socket?.connected) {
        console.log('🔌 Disconnecting Socket.IO - user not authenticated');
        disconnect();
      }
    }
  }, [token, status, userData, authFailed]);

  // Expose retry function globally for debugging
  // useEffect(() => {
  //   (window as any).retrySocketConnection = retryConnection;
  //   (window as any).disconnectSocket = disconnect;
  //   // console.log('🔌 Debug functions available:');
  //   // console.log(
  //   //   '🔌 - window.retrySocketConnection() - Manually retry socket connection',
  //   // );
  //   // console.log('🔌 - window.disconnectSocket() - Manually disconnect socket');
  // }, [retryConnection, disconnect]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Stop heartbeat on unmount
      stopHeartbeat();
      disconnect();
    };
  }, []);
  const value: SocketContextType = {
    socket,
    isConnected,
    emitEvent,
    connect,
    disconnect,
    listingFetched,
    retryConnection, // Add retry function to context
  };

  return (
    <SocketContext.Provider value={value}>{children}</SocketContext.Provider>
  );
};
